import { useEffect, useRef } from 'react';

import { Col, DatePicker, Form, Row, Select } from 'antd';
import type { FormInstance } from 'antd/es/form';
import moment, { type Moment } from 'moment';

import { useWorkJourneyContext } from '@src/packages/workJourney/contexts';
import type { WorkJourneyShiftDayConfig, WorkJourneyWorkShift } from '@src/packages/workJourney/types';
import IntlMessages from '@src/util/IntlMessages';

import styles from '../../../styles.module.less';

const { Option } = Select;

interface ShiftJourneySelectorProps {
  form: FormInstance;
}

const ShiftJourneySelector = ({ form }: ShiftJourneySelectorProps) => {
  const shiftEndDateRef = useRef(null);
  const { workJourneyForm, setWorkJourneyForm, filteredWorkShifts } = useWorkJourneyContext();

  const getShiftStartTime = (shift: WorkJourneyWorkShift | undefined) => {
    try {
      const days = shift?.recurrence?.weeks?.[0]?.days;
      if (!days) return null;
      const firstDay = Object.values(days)[0] as WorkJourneyShiftDayConfig | undefined;
      const startBreakTime = firstDay?.startBreakTime;
      const formated = moment(startBreakTime, 'HH:mm');
      if (formated.isValid()) return formated;
      return null;
    } catch (error) {
      return null;
    }
  };

  const handleChange = (value: string) => {
    const shift = filteredWorkShifts.find(s => s.id === value);
    if (!shift) return;
    const breakStartTime = getShiftStartTime(shift);
    setWorkJourneyForm({
      ...workJourneyForm,
      type: shift.type ?? workJourneyForm.type,
      workShift: shift,
      breakStartTime,
    });
  };

  const disableEndDate = (current: Moment | null) => {
    return (
      !!current &&
      (current < moment().startOf('day') ||
        (!!workJourneyForm.shiftStartDate && current < workJourneyForm.shiftStartDate) ||
        current < moment().startOf('day'))
    );
  };

  useEffect(() => {
    if (workJourneyForm.workShift) {
      if (form && !!filteredWorkShifts.length) {
        const shift = filteredWorkShifts.find(s => s.id === workJourneyForm.workShift?.id);
        if (shift) {
          form.setFieldsValue({ workShift: shift.id });
        }
      }
    }
    if (workJourneyForm.shiftStartDate) {
      const shiftStartDate = moment(workJourneyForm.shiftStartDate).startOf('day');
      form.setFieldsValue({ shiftStartDate });
    }
    if (workJourneyForm.shiftEndDate) {
      const shiftEndDate = moment(workJourneyForm.shiftEndDate).startOf('day');
      form.setFieldsValue({ shiftEndDate });
    }
  }, [workJourneyForm.workShift, filteredWorkShifts, form]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Row className={styles.formSectionRow}>
        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
          <Form.Item
            name="workShift"
            label={<IntlMessages id="workJourney.shift.selectShift" />}
            rules={[
              { required: true, message: <IntlMessages id="workJourney.validation.workShiftRequired" /> },
            ]}
          >
            <Select
              placeholder={<IntlMessages id="workJourney.shift.selectShiftPlaceholder" />}
              onChange={handleChange}
              value={workJourneyForm.workShift?.id || undefined}
            >
              {filteredWorkShifts.map(shift => (
                <Option key={shift.id} value={shift.id}>
                  {shift.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.formSectionRow}>
        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
          <Form.Item
            name="shiftStartDate"
            label={<IntlMessages id="workJourney.shift.startDate" />}
            rules={[
              { required: true, message: <IntlMessages id="workJourney.validation.startDateRequired" /> },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              onChange={e => {
                setWorkJourneyForm({ ...workJourneyForm, shiftStartDate: e });
                if (e && workJourneyForm.shiftEndDate) {
                  if (e > workJourneyForm.shiftEndDate) {
                    setWorkJourneyForm({
                      ...workJourneyForm,
                      shiftStartDate: e.startOf('day'),
                      shiftEndDate: e.startOf('day'),
                    });
                  }
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={12} lg={12} xl={12}>
          <Form.Item name="shiftEndDate" label={<IntlMessages id="workJourney.shift.endDate" />}>
            <DatePicker
              ref={shiftEndDateRef}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={workJourneyForm.shiftEndDate}
              disabledDate={disableEndDate}
              onChange={e => {
                setWorkJourneyForm({
                  ...workJourneyForm,
                  shiftEndDate: e ? e.startOf('day') : null,
                });
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default ShiftJourneySelector;
